import React from 'react'
import HeroCallToActionSection from '../components/sections/HeroCallToActionSection'

const HeroCtaDemoPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Hero Call-to-Action Section
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Dramatic hero section with full-width background image, dark overlay, and powerful call-to-action elements. 
            Features vertical dividers, elegant typography, and dual CTAs based on exact Figma specifications.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Visual Drama</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Full-width background image</li>
                <li>• Professional dark overlay</li>
                <li>• White text for maximum contrast</li>
                <li>• 600px exact height</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Structural Elements</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Vertical divider lines</li>
                <li>• Centered content layout</li>
                <li>• Dual CTA buttons</li>
                <li>• Underline accents</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Hudson Club Messaging</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Effortless homeownership focus</li>
                <li>• Premium brand positioning</li>
                <li>• Clear value proposition</li>
                <li>• Action-oriented CTAs</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Component Showcase */}
      <HeroCallToActionSection />

      {/* Design Analysis */}
      <div className="bg-gray-50 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Design Breakdown & Implementation
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            
            {/* Design Specifications */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                🎨 Figma Specifications
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Section Height</span>
                  <span className="text-gray-600">600px</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Background</span>
                  <span className="text-gray-600">rgb(30, 29, 28)</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Text Color</span>
                  <span className="text-gray-600">White (rgb(255,255,255))</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Container Width</span>
                  <span className="text-gray-600">1200px max</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Image Size</span>
                  <span className="text-gray-600">1920x1280px</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-gray-700">Overlay Opacity</span>
                  <span className="text-gray-600">75% dark</span>
                </div>
              </div>
            </div>

            {/* Advanced Features */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                ⚡ Advanced Features
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Parallax Background</p>
                    <p className="text-gray-600 text-sm">Image positioned with offset for depth</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Vertical Dividers</p>
                    <p className="text-gray-600 text-sm">Structured layout with white lines</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-purple-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Animated CTAs</p>
                    <p className="text-gray-600 text-sm">Hover effects and underline accents</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Ambient Lighting</p>
                    <p className="text-gray-600 text-sm">Subtle radial gradient effects</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Mobile Optimized</p>
                    <p className="text-gray-600 text-sm">Responsive design with mobile layout</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Usage Scenarios */}
          <div className="bg-white rounded-lg p-8 shadow-sm mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              🎯 Perfect Use Cases
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 text-xl">🏠</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Landing Pages</h4>
                <p className="text-gray-600 text-sm">Powerful first impression with clear CTAs</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-600 text-xl">📞</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Contact Sections</h4>
                <p className="text-gray-600 text-sm">Drive inquiries and consultations</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-purple-600 text-xl">✨</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Brand Statements</h4>
                <p className="text-gray-600 text-sm">Communicate core value propositions</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-yellow-600 text-xl">🎯</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Conversion Points</h4>
                <p className="text-gray-600 text-sm">High-impact lead generation sections</p>
              </div>
            </div>
          </div>

          {/* Implementation Code */}
          <div className="bg-gray-900 rounded-lg p-8 text-white">
            <h3 className="text-2xl font-bold mb-6">
              📝 Implementation Guide
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
              <div>
                <h4 className="text-lg font-semibold mb-4 text-gray-300">Basic Usage:</h4>
                <pre className="bg-gray-800 rounded p-4 text-sm overflow-x-auto">
                  <code>{`import HeroCallToActionSection from 
  '../components/sections/HeroCallToActionSection'

const LandingPage = () => {
  return (
    <div>
      <HeroCallToActionSection />
    </div>
  )
}`}</code>
                </pre>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold mb-4 text-gray-300">Key Customizations:</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li>• Replace background image URL</li>
                  <li>• Modify headline and supporting text</li>
                  <li>• Update CTA button text and actions</li>
                  <li>• Adjust overlay opacity</li>
                  <li>• Customize vertical divider positioning</li>
                  <li>• Change animation timings</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <h4 className="text-lg font-semibold mb-4 text-gray-300">Image Requirements:</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="font-medium text-white">Minimum Size</div>
                  <div className="text-gray-400">1920x1080px</div>
                </div>
                <div>
                  <div className="font-medium text-white">Recommended</div>
                  <div className="text-gray-400">1920x1280px</div>
                </div>
                <div>
                  <div className="font-medium text-white">Format</div>
                  <div className="text-gray-400">JPG, PNG, WebP</div>
                </div>
                <div>
                  <div className="font-medium text-white">Quality</div>
                  <div className="text-gray-400">High resolution</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HeroCtaDemoPage 